// src/stores/integrations.ts
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { useAxios } from '../composables/axios';
import { useAuthStore } from './auth';
import { AxiosError, AxiosRequestConfig } from 'axios';
import Swal from 'sweetalert2';
import { Integration, CreateIntegrationInput, UpdateIntegrationInput, SqlConfig, ApiConfig } from '@/types/integration';


interface ApiResponse {
  message?: string;
  data?: any;
  status?: number;
}

interface ErrorResponse {
  message?: string;
  error?: string;
}

interface IntegrationStats {
  queryCount: number;
  avgQueryTime: string;
  tableCount: number;
  lastSyncDate: string | Date | null;
}

interface IntegrationLog {
  id: string;
  type: 'connection' | 'query' | 'update' | 'error';
  description: string;
  timestamp: Date | string;
  status: 'success' | 'error' | 'warning';
}

export const useIntegrationStore = defineStore('integrations', () => {
  const { get, post, patch, del } = useAxios();
  const authStore = useAuthStore();
  const integrations = ref<Integration[]>([]);
  const selectedIntegration = ref<Integration | null>(null);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // Gösterilen entegrasyonları filtrele
  const filteredIntegrations = (
    searchQuery: string = '',
    typeFilter: string = '',
    statusFilter: string = ''
  ) => {
    return integrations.value.filter((integration: Integration) => {
      // Arama filtreleri uygula
      const searchLower = searchQuery.toLowerCase();
      const nameMatch = integration.name.toLowerCase().includes(searchLower);
      const serverMatch =
        (integration.config as any).server?.toLowerCase().includes(searchLower) || false;
      const endpointMatch =
        (integration.config as any).endpoint?.toLowerCase().includes(searchLower) || false;

      const searchMatches = searchQuery === '' || nameMatch || serverMatch || endpointMatch;

      // Tip filtresi
      const typeMatches = typeFilter === '' || integration.type === typeFilter;

      // Durum filtresi
      const statusMatches = statusFilter === '' || integration.status === statusFilter;

      return searchMatches && typeMatches && statusMatches;
    });
  };

  // Tüm entegrasyonları getir
  const fetchAllIntegrations = async () => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<any>('/integration/0.0.1', {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      integrations.value = response || [];
      return integrations.value;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyonlar yüklenirken hata oluştu';

      console.error('Error fetching integrations:', error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // ID'ye göre tek bir entegrasyon getir
  const fetchIntegrationById = async (id: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<Integration[]>(`/integration/0.0.1?id=${id}`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      if (response && response.length > 0) {
        selectedIntegration.value = response[0];
        return selectedIntegration.value;
      }

      return null;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon bilgileri yüklenirken hata oluştu';

      console.error(`Error fetching integration ${id}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Yeni entegrasyon oluştur
  const createIntegration = async (data: CreateIntegrationInput) => {
    isLoading.value = true;
    error.value = null;

    // Use the standard approach for all integrations
    const formData = new FormData();
    formData.append('name', data.name);
    formData.append('type', data.type);
    formData.append('subType', data.subType);
    formData.append('config', JSON.stringify(data.config));
    
    const headers: AxiosRequestConfig['headers'] = {
      Authorization: `Bearer ${authStore.user?.token}`,
      'Content-Type': 'multipart/form-data'
    };

    try {
      const response = await post<any>('/integration/0.0.1', formData, {
        headers
      });

      await Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Entegrasyon başarıyla oluşturuldu'
      });

      // Yeni oluşturulan entegrasyonun ID'sini al
      const newId = response;

      // Entegrasyonları yeniden yükleyerek cache'i güncelle
      await fetchAllIntegrations();

      return newId;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon oluşturulurken hata oluştu';

      await Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Entegrasyon güncelle
  const updateIntegration = async (data: UpdateIntegrationInput) => {
    isLoading.value = true;
    error.value = null;

    try {
      await patch<ApiResponse>('/integration/0.0.1', data, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      await Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Entegrasyon başarıyla güncellendi'
      });

      // Entegrasyonları yeniden yükleyerek cache'i güncelle
      await fetchAllIntegrations();

      return true;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon güncellenirken hata oluştu';

      await Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Entegrasyon sil
  const deleteIntegration = async (id: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      await del<ApiResponse>(`/integration/0.0.1/${id}`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      // Yerel listeyi güncelle
      integrations.value = integrations.value.filter((i: Integration) => i.id !== id);

      await Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Entegrasyon başarıyla silindi'
      });

      return true;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon silinirken hata oluştu';

      await Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Toplu silme
  const bulkDeleteIntegrations = async (ids: string[]) => {
    isLoading.value = true;
    error.value = null;

    try {
      await del<ApiResponse>('/integration/0.0.1', {
        data: { ids },
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      // Yerel listeyi güncelle
      integrations.value = integrations.value.filter((i: Integration) => !ids.includes(i.id));

      await Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Seçili entegrasyonlar başarıyla silindi'
      });

      return true;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyonlar silinirken hata oluştu';

      await Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Bağlantı testi
  const testConnection = async (id: string,isSwall:boolean=true) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await post<ApiResponse>(
        `/integration/0.0.1/test`,
        {
          id: id.toString()
        },
        {
          headers: {
            Authorization: `Bearer ${authStore.user?.token}`
          }
        }
      );


      if(!isSwall) return true;
      // Başarılı test durumunda
      await Swal.fire({
        icon: 'success',
        title: 'Bağlantı Başarılı',
        text: 'Entegrasyon bağlantısı başarıyla test edildi'
      });

      // Entegrasyonları yeniden yükle (durum değişmiş olabilir)
      await fetchAllIntegrations();

      // TypeScript hatasını gidermek için null kontrolü ekledik
      return response && response.data ? response.data : null;
    } catch (err) {
      if(!isSwall) return false;

      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Bağlantı testi başarısız oldu';

      await Swal.fire({
        icon: 'error',
        title: 'Bağlantı Hatası',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const testGenericConnection = async (
    type: string,
    subType: string,
    config: SqlConfig | ApiConfig
  ) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await post<any>(
        `/integration/0.0.1/test/generic`,
        {
          type: type,
          subType: subType,
          config: config
        },
        {
          headers: {
            Authorization: `Bearer ${authStore.user?.token}`
          }
        }
      );

      // Başarılı test durumunda
      await Swal.fire({
        icon: 'success',
        title: 'Bağlantı Başarılı',
        text: 'Entegrasyon bağlantısı başarıyla test edildi'
      });

      return response.body;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Bağlantı testi başarısız oldu';

      await Swal.fire({
        icon: 'error',
        title: 'Bağlantı Hatası',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Entegrasyona ait tabloları getir fonksiyonu
  const fetchIntegrationTables = async (integrationId: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<any>(`/integration/0.0.1/${integrationId}/tables`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      return response || [];
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon tabloları yüklenirken hata oluştu';

      console.error(`Error fetching tables for integration ${integrationId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  const fetchIntegrationTablesAll = async (integrationId: string, {offset, limit, type, search}: {offset?: number, limit?: number, type?: 'tables' | 'views' | 'all', search?: string}) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<{
        rows: Array<{
            table_name: string;
        }>;
        count: number;
    }>(`/integration/0.0.1/${integrationId}/tables/all`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        },
        params: {
          offset,
          limit,
          type,
          search
        }
      });

      return response || [];
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon tabloları yüklenirken hata oluştu';

      console.error(`Error fetching tables for integration ${integrationId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Entegrasyona ait tabloları getir fonksiyonu
  const fetchIntegrationTablesDataTop100 = async (tableId: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<any>(`/integration/0.0.1/${tableId}/tables/data`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      return response || [];
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon tabloları yüklenirken hata oluştu';

      console.error(`Error fetching tables for integration ${tableId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Tablo yorumunu güncelle
  const updateTableComment = async (tableId: string, comment: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await post<any>(
        `/integration/0.0.1/tables/comment`,
        {
          id: tableId,
          comment: comment
        },
        {
          headers: {
            Authorization: `Bearer ${authStore.user?.token}`
          }
        }
      );

      return response?.success || false;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Tablo yorumu güncellenirken hata oluştu';

      console.error(`Error updating comment for table ${tableId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Toplu tablo yorumu güncelleme
  const bulkUpdateTableComments = async (tableComments: Record<string, string>) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await post<any>(
        `/integration/0.0.1/tables/bulk-comment`,
        {
          tableComments
        },
        {
          headers: {
            Authorization: `Bearer ${authStore.user?.token}`
          }
        }
      );

      return response?.success || false;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Tablo yorumları güncellenirken hata oluştu';

      console.error('Error updating table comments:', error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Kolon yorumunu güncelle
  const updateColumnComment = async (tableId: string, columnName: string, comment: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await post<any>(
        `/integration/0.0.1/tables/column/comment`,
        {
          id: tableId,
          columnName: columnName,
          comment: comment
        },
        {
          headers: {
            Authorization: `Bearer ${authStore.user?.token}`
          }
        }
      );

      return response?.success || false;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Kolon yorumu güncellenirken hata oluştu';

      console.error(
        `Error updating comment for column ${columnName} in table ${tableId}:`,
        error.value
      );
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Toplu kolon yorumu güncelleme
  const bulkUpdateColumnComments = async (
    tableId: string,
    columnComments: Record<string, string>
  ) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await post<any>(
        `/integration/0.0.1/tables/column/bulk-comment`,
        {
          tableId,
          columnComments
        },
        {
          headers: {
            Authorization: `Bearer ${authStore.user?.token}`
          }
        }
      );

      return response?.success || false;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Kolon yorumları güncellenirken hata oluştu';

      console.error(`Error updating column comments for table ${tableId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Tablo için kolon yorumlarını getir
  const getColumnComments = async (tableId: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<any>(`/integration/0.0.1/tables/column/comments/${tableId}`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      return response || {};
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Kolon yorumları yüklenirken hata oluştu';

      console.error(`Error fetching column comments for table ${tableId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Entegrasyona ait tabloları güncelle
  const updateIntegrationTables = async (integrationId: string, selectedTables: string[]) => {
    isLoading.value = true;
    error.value = null;

    try {
      await patch(
        `/integration/0.0.1/${integrationId}/tables/update`,
        {
          tableNames: selectedTables
        },
        {
          headers: {
            Authorization: `Bearer ${authStore.user?.token}`
          }
        }
      );

    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Entegrasyon tabloları güncellenirken hata oluştu';

      console.error(`Error updating tables for integration ${integrationId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Fetch integration stats
  const fetchIntegrationStats = async (integrationId: string): Promise<IntegrationStats> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<IntegrationStats>(`/integration/0.0.1/${integrationId}/stats`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      return response || {
        queryCount: 0,
        avgQueryTime: '0ms',
        tableCount: 0,
        lastSyncDate: null
      };
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Integration stats could not be loaded';

      console.error(`Error fetching stats for integration ${integrationId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Fetch integration logs/activities
  const fetchIntegrationLogs = async (integrationId: string): Promise<IntegrationLog[]> => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await get<IntegrationLog[]>(`/integration/0.0.1/${integrationId}/logs`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      return response || [];
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Integration logs could not be loaded';

      console.error(`Error fetching logs for integration ${integrationId}:`, error.value);
      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Delete a single table
  const deleteTable = async (tableId: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await del<ApiResponse>(`/integration/0.0.1/tables/${tableId}`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      await Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: response?.message || 'Tablo başarıyla silindi',
        timer: 1500,
        showConfirmButton: false
      });

      return response;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Tablo silinirken hata oluştu';

      await Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Bulk delete multiple tables
  const bulkDeleteTables = async (tableIds: string[]) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await post<ApiResponse>(`/integration/0.0.1/tables/bulk-delete`, {
        tableIds
      }, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      await Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Tablolar başarıyla silindi'
      });

      return response;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Tablolar silinirken hata oluştu';

      await Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  // Delete all tables for an integration
  const deleteAllTables = async (integrationId: string) => {
    isLoading.value = true;
    error.value = null;

    try {
      const response = await del<ApiResponse>(`/integration/0.0.1/${integrationId}/tables`, {
        headers: {
          Authorization: `Bearer ${authStore.user?.token}`
        }
      });

      await Swal.fire({
        icon: 'success',
        title: 'Başarılı',
        text: 'Tüm tablolar başarıyla silindi'
      });

      return response;
    } catch (err) {
      const axiosError = err as AxiosError<ErrorResponse>;
      error.value =
        axiosError.response?.data?.message ||
        axiosError.response?.data?.error ||
        'Tablolar silinirken hata oluştu';

      await Swal.fire({
        icon: 'error',
        title: 'Hata',
        text: error.value
      });

      throw err;
    } finally {
      isLoading.value = false;
    }
  };

  return {
    integrations,
    selectedIntegration,
    isLoading,
    error,
    filteredIntegrations,
    fetchAllIntegrations,
    fetchIntegrationById,
    createIntegration,
    updateIntegration,
    deleteIntegration,
    bulkDeleteIntegrations,
    testConnection,
    testGenericConnection,
    fetchIntegrationTables,
    fetchIntegrationTablesDataTop100,
    updateTableComment,
    bulkUpdateTableComments,
    updateColumnComment,
    bulkUpdateColumnComments,
    getColumnComments,
    fetchIntegrationTablesAll,
    updateIntegrationTables,
    fetchIntegrationStats,
    fetchIntegrationLogs,
    // New table delete functions
    deleteTable,
    bulkDeleteTables,
    deleteAllTables
  };
});